import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, History, Lock, MoreHorizontal, Sparkles, UserRound } from 'lucide-react';
import { Avatar, Menu, Pill } from '../../../components/ui';
import type { StanceView } from '../../../types/issues';
import {
  CONFIDENCE_LABEL,
  POSITION_ORDER,
  SOURCE_LABEL,
  positionLabel,
  subjectHref,
  timeAgo,
} from '../components/format';
import { InfluenceBar } from '../components/PositionMark';
import { ConfidentialityDialog, LockMarker } from '../../confidentiality';

interface StanceCardProps {
  stance: StanceView;
  /** Role and organization, when the directory knows them */
  line?: string;
  onMove: (position: number) => void;
  onHistory: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  dragging: boolean;
}

/**
 * One actor on the board: who they are, how much they can move this issue,
 * what the reading rests on and when it was last checked.
 */
export const StanceCard: React.FC<StanceCardProps> = ({
  stance,
  line,
  onMove,
  onHistory,
  onDragStart,
  onDragEnd,
  dragging,
}) => {
  const { subject } = stance;
  const [accessOpen, setAccessOpen] = React.useState(false);
  const stale = stance.confidence === 'low';

  return (
    <div
      draggable
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = 'move';
        // Firefox refuses to start a drag without payload
        event.dataTransfer.setData('text/plain', `${subject.type}:${subject.id}`);
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      className={`group rounded-xl border border-neutral-200 bg-white p-2.5 transition-shadow ${
        dragging ? 'opacity-40' : 'hover:shadow-[0_2px_10px_-4px_rgba(0,0,0,0.12)]'
      }`}
    >
      <div className="flex items-start gap-2">
        <Avatar name={subject.label} kind={subject.type} size="sm" className="mt-0.5 cursor-grab active:cursor-grabbing" />
        <div className="min-w-0 flex-1">
          <span className="flex min-w-0 items-center gap-1.5">
            <Link
              to={subjectHref(subject)}
              className="block truncate text-[13px] text-ink transition-colors hover:text-primary-700"
            >
              {subject.label}
            </Link>
            <LockMarker record={stance} />
          </span>
          <p className="mt-0.5 truncate text-[11.5px] text-neutral-400">
            {line || (subject.type === 'organization' ? 'Organization' : 'Person')}
          </p>
        </div>
        <Menu
          align="right"
          items={[
            ...POSITION_ORDER.filter((position) => position !== stance.position).map((position) => ({
              label: `Move to ${positionLabel(position)}`,
              onSelect: () => onMove(position),
            })),
            { label: 'Position history', icon: History, onSelect: onHistory },
            { label: 'Access', icon: Lock, onSelect: () => setAccessOpen(true) },
          ]}
          trigger={(props) => (
            <button
              type="button"
              aria-label={`Actions for ${subject.label}`}
              className="-mr-1 -mt-0.5 inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
              {...props}
            >
              <MoreHorizontal className="h-4 w-4" strokeWidth={1.75} />
            </button>
          )}
        />
      </div>

      <InfluenceBar value={stance.issueInfluence} className="mt-2" />

      {stance.rationale && (
        <p className="mt-2 line-clamp-2 [overflow-wrap:anywhere] text-[12px] leading-snug text-neutral-500" title={stance.rationale}>
          {stance.rationale}
        </p>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-neutral-400">
        {stance.source === 'ai' ? (
          <Pill tone="violet" title="Proposed by AI and accepted by an analyst">
            <Sparkles className="h-2.5 w-2.5" strokeWidth={2} />
            AI
          </Pill>
        ) : (
          <span title={`Source · ${SOURCE_LABEL[stance.source]}`}>{SOURCE_LABEL[stance.source]}</span>
        )}
        <span aria-hidden="true">·</span>
        <span className={stale ? 'text-amber-600' : undefined} title="Confidence in this reading">
          {CONFIDENCE_LABEL[stance.confidence]} confidence
        </span>
        {stance.asOf && (
          <>
            <span aria-hidden="true">·</span>
            <span title={new Date(stance.asOf).toLocaleString()}>{timeAgo(stance.asOf)}</span>
          </>
        )}
        {stance.evidenceUrl && (
          <a
            href={stance.evidenceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-neutral-400 transition-colors hover:text-ink"
            title="Evidence"
          >
            <ExternalLink className="h-3 w-3" strokeWidth={1.75} />
            <span className="sr-only">Evidence for {subject.label}</span>
          </a>
        )}
        {stance.recordedBy && (
          <span className="inline-flex items-center gap-1" title={`Recorded by ${stance.recordedBy.name}`}>
            <UserRound className="h-3 w-3" strokeWidth={1.75} />
            <span className="sr-only">Recorded by {stance.recordedBy.name}</span>
          </span>
        )}
      </div>
    
      <ConfidentialityDialog
        open={accessOpen}
        onClose={() => setAccessOpen(false)}
        entityType="issue_stance"
        entityId={stance.id}
        label={subject.label}
      />
    </div>
  );
};

export default StanceCard;
